import React, { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { toast } from "sonner";

/**
 * OCI V2 — Gift Aid Panel
 * Reuses /api/gift-aid/* endpoints. Strictly additive. Read-only by default.
 * Owner/Manager/Platform Owner roles supported via existing JWT auth.
 */
const formatGBP = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);
const formatPct = (v) => `${(v || 0).toFixed(1)}%`;
const formatInt = (v) => new Intl.NumberFormat("en-GB").format(v || 0);

const KPI = ({ label, value, tone = "default", subtitle, testid }) => {
  const toneClass = {
    default: "bg-white border-slate-200 text-slate-900",
    positive: "bg-emerald-50 border-emerald-200 text-emerald-900",
    warning: "bg-amber-50 border-amber-200 text-amber-900",
    danger: "bg-rose-50 border-rose-200 text-rose-900",
    accent: "bg-indigo-50 border-indigo-200 text-indigo-900",
  }[tone];
  return (
    <div className={`rounded-xl border p-4 ${toneClass}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {subtitle && <div className="text-xs opacity-70 mt-1">{subtitle}</div>}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    draft: "bg-slate-100 text-slate-700",
    submitted: "bg-blue-100 text-blue-700",
    accepted: "bg-indigo-100 text-indigo-700",
    paid: "bg-emerald-100 text-emerald-700",
    rejected: "bg-rose-100 text-rose-700",
    active: "bg-emerald-100 text-emerald-700",
    withdrawn: "bg-rose-100 text-rose-700",
    expired: "bg-slate-100 text-slate-500",
  };
  return <Badge className={map[status] || "bg-slate-100"}>{status}</Badge>;
};

export const GiftAidPanel = ({ scope = "organisation", campaignId = null, embedded = false }) => {
  const [dashboard, setDashboard] = useState(null);
  const [breakdownCamp, setBreakdownCamp] = useState([]);
  const [breakdownLoc, setBreakdownLoc] = useState([]);
  const [breakdownFr, setBreakdownFr] = useState([]);
  const [batches, setBatches] = useState([]);
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("summary");
  const [batchAction, setBatchAction] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = campaignId ? { campaignId } : {};
      const [d, bc, bl, bf, bb, decl] = await Promise.all([
        apiClient.get("/gift-aid/dashboard", { params }),
        apiClient.get("/gift-aid/breakdown", { params: { by: "campaign" } }),
        apiClient.get("/gift-aid/breakdown", { params: { by: "location" } }),
        apiClient.get("/gift-aid/breakdown", { params: { by: "fundraiser" } }),
        apiClient.get("/gift-aid/batches"),
        apiClient.get("/gift-aid/declarations"),
      ]);
      setDashboard(d.data);
      setBreakdownCamp((bc.data?.groups || []));
      setBreakdownLoc((bl.data?.groups || []));
      setBreakdownFr((bf.data?.groups || []));
      setBatches(bb.data || []);
      setDeclarations(decl.data || []);
    } catch (e) {
      console.warn("Gift Aid load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [campaignId]);

  const createBatch = async () => {
    try {
      setBatchAction("creating");
      await apiClient.post("/gift-aid/batches", campaignId ? { campaignId } : {});
      toast.success("Claim batch created");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create batch");
    } finally {
      setBatchAction(null);
    }
  };

  const transitionBatch = async (id, status) => {
    try {
      setBatchAction(`${id}:${status}`);
      await apiClient.post(`/gift-aid/batches/${id}/transition`, { status });
      toast.success(`Batch moved to ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${status}`);
    } finally {
      setBatchAction(null);
    }
  };

  const withdrawDeclaration = async (id) => {
    if (!window.confirm("Withdraw this declaration? Active eligibility on unbatched conversions will be cleared.")) return;
    try {
      await apiClient.delete(`/gift-aid/declarations/${id}`, { params: { reason: "donor_request" } });
      toast.success("Declaration withdrawn");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdraw failed");
    }
  };

  if (loading && !dashboard) {
    return (
      <Card data-testid="gift-aid-panel-loading">
        <CardContent className="p-6 text-sm text-slate-500">Loading Gift Aid metrics…</CardContent>
      </Card>
    );
  }

  const d = dashboard || {};

  const summary = (
    <div className="space-y-4" data-testid="gift-aid-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total Donations" value={formatInt(d.totalDonations)} subtitle={formatGBP(d.totalDonationValue)} testid="ga-kpi-total-donations" />
        <KPI label="Gift Aid Eligible" value={formatInt(d.giftAidEligibleDonations)} subtitle={formatPct(d.giftAidEligiblePct)} tone="accent" testid="ga-kpi-eligible" />
        <KPI label="Recoverable" value={formatGBP(d.giftAidRecoverable)} tone="positive" testid="ga-kpi-recoverable" />
        <KPI label="Paid" value={formatGBP(d.giftAidPaid)} tone="positive" testid="ga-kpi-paid" />
        <KPI label="Submitted" value={formatGBP(d.giftAidSubmitted)} testid="ga-kpi-submitted" />
        <KPI label="Outstanding" value={formatGBP(d.giftAidOutstanding)} tone="warning" testid="ga-kpi-outstanding" />
        <KPI label="Opportunity Lost" value={formatGBP(d.giftAidOpportunityLost)} tone="danger" subtitle="No declaration captured" testid="ga-kpi-opportunity-lost" />
        <KPI label="Rate" value={`${((d.giftAidRate || 0.25) * 100).toFixed(0)}%`} subtitle="HMRC standard" testid="ga-kpi-rate" />
      </div>
      <div className="flex justify-end">
        <Button
          onClick={createBatch}
          disabled={batchAction === "creating" || (d.giftAidRecoverable || 0) <= (d.giftAidSubmitted || 0) + (d.giftAidPaid || 0)}
          data-testid="ga-create-batch-btn"
        >
          {batchAction === "creating" ? "Creating…" : "Create claim batch from unbatched donations"}
        </Button>
      </div>
    </div>
  );

  const breakdownTable = (rows, keyLabel) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{keyLabel}</TableHead>
          <TableHead className="text-right">Donations</TableHead>
          <TableHead className="text-right">Donation Value</TableHead>
          <TableHead className="text-right">Eligible</TableHead>
          <TableHead className="text-right">Eligible %</TableHead>
          <TableHead className="text-right">Recoverable</TableHead>
          <TableHead className="text-right">Opportunity Lost</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {(rows || []).map((r, i) => (
          <TableRow key={r.key || i} data-testid={`ga-breakdown-row-${i}`}>
            <TableCell className="font-mono text-xs">{r.key || "(unassigned)"}</TableCell>
            <TableCell className="text-right">{formatInt(r.totalDonations)}</TableCell>
            <TableCell className="text-right">{formatGBP(r.totalDonationValue)}</TableCell>
            <TableCell className="text-right">{formatInt(r.eligibleCount)}</TableCell>
            <TableCell className="text-right">{formatPct(r.eligiblePct)}</TableCell>
            <TableCell className="text-right text-emerald-700">{formatGBP(r.estimatedClaim)}</TableCell>
            <TableCell className="text-right text-rose-700">{formatGBP(r.giftAidOpportunityLost)}</TableCell>
          </TableRow>
        ))}
        {(!rows || rows.length === 0) && (
          <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">No data</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );

  const batchesView = (
    <div className="space-y-3" data-testid="ga-batches-view">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Reference</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Donations</TableHead>
            <TableHead className="text-right">Donation Total</TableHead>
            <TableHead className="text-right">Claim Amount</TableHead>
            <TableHead className="text-right">Paid Amount</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {batches.map((b, i) => (
            <TableRow key={b.id} data-testid={`ga-batch-row-${i}`}>
              <TableCell className="font-mono text-xs">{b.batchReference}</TableCell>
              <TableCell><StatusBadge status={b.status} /></TableCell>
              <TableCell className="text-right">{formatInt(b.donationCount)}</TableCell>
              <TableCell className="text-right">{formatGBP(b.donationTotal)}</TableCell>
              <TableCell className="text-right text-emerald-700">{formatGBP(b.claimAmount)}</TableCell>
              <TableCell className="text-right">{b.paidAmount ? formatGBP(b.paidAmount) : "—"}</TableCell>
              <TableCell className="text-xs">{(b.createdAt || "").slice(0, 10)}</TableCell>
              <TableCell className="space-x-1">
                {b.status === "draft" && (
                  <Button size="sm" variant="secondary" onClick={() => transitionBatch(b.id, "submitted")}
                          disabled={!!batchAction} data-testid={`ga-batch-submit-${i}`}>
                    Submit
                  </Button>
                )}
                {b.status === "submitted" && (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => transitionBatch(b.id, "accepted")}
                            disabled={!!batchAction} data-testid={`ga-batch-accept-${i}`}>
                      Accept
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => transitionBatch(b.id, "rejected")}
                            disabled={!!batchAction} data-testid={`ga-batch-reject-${i}`}>
                      Reject
                    </Button>
                  </>
                )}
                {b.status === "accepted" && (
                  <Button size="sm" onClick={() => transitionBatch(b.id, "paid")}
                          disabled={!!batchAction} data-testid={`ga-batch-pay-${i}`}>
                    Mark Paid
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {batches.length === 0 && (
            <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-6">No claim batches yet</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );

  const declarationsView = (
    <Table data-testid="ga-declarations-view">
      <TableHeader>
        <TableRow>
          <TableHead>Donor</TableHead>
          <TableHead>Postcode</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {declarations.map((dx, i) => (
          <TableRow key={dx.id} data-testid={`ga-declaration-row-${i}`}>
            <TableCell>{dx.donorName || "—"}</TableCell>
            <TableCell className="font-mono text-xs">{dx.donorPostcode || "—"}</TableCell>
            <TableCell><StatusBadge status={dx.status} /></TableCell>
            <TableCell className="text-xs">{dx.declarationType}</TableCell>
            <TableCell className="text-xs">{(dx.declarationDate || dx.createdAt || "").slice(0, 10)}</TableCell>
            <TableCell>
              {dx.status === "active" && (
                <Button size="sm" variant="destructive"
                        onClick={() => withdrawDeclaration(dx.id)}
                        data-testid={`ga-decl-withdraw-${i}`}>
                  Withdraw
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
        {declarations.length === 0 && (
          <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">No declarations yet</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );

  const body = (
    <Tabs value={activeView} onValueChange={setActiveView}>
      <TabsList>
        <TabsTrigger value="summary" data-testid="ga-tab-summary">Summary</TabsTrigger>
        <TabsTrigger value="campaign" data-testid="ga-tab-campaign">By Campaign</TabsTrigger>
        <TabsTrigger value="location" data-testid="ga-tab-location">By Location</TabsTrigger>
        <TabsTrigger value="fundraiser" data-testid="ga-tab-fundraiser">By Fundraiser</TabsTrigger>
        <TabsTrigger value="batches" data-testid="ga-tab-batches">Claim Batches</TabsTrigger>
        <TabsTrigger value="declarations" data-testid="ga-tab-declarations">Declarations</TabsTrigger>
      </TabsList>
      <TabsContent value="summary" className="mt-4">{summary}</TabsContent>
      <TabsContent value="campaign" className="mt-4">{breakdownTable(breakdownCamp, "Campaign")}</TabsContent>
      <TabsContent value="location" className="mt-4">{breakdownTable(breakdownLoc, "Location")}</TabsContent>
      <TabsContent value="fundraiser" className="mt-4">{breakdownTable(breakdownFr, "Fundraiser")}</TabsContent>
      <TabsContent value="batches" className="mt-4">{batchesView}</TabsContent>
      <TabsContent value="declarations" className="mt-4">{declarationsView}</TabsContent>
    </Tabs>
  );

  if (embedded) return body;
  return (
    <Card data-testid="gift-aid-panel">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Gift Aid {campaignId ? "— Campaign" : "— Organisation"}</span>
          <Button size="sm" variant="ghost" onClick={load} data-testid="ga-refresh-btn">Refresh</Button>
        </CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

export default GiftAidPanel;
